import { Controller } from "stimulus";
import consumer from "../channels/consumer";

export default class extends Controller {
  static targets = ["inputField", "numberDisplay", "comparisonDisplay", "comparisonSelector", "comparisonSelect"];

  connect() {
    this.blockId = this.element.dataset.blockId;
    this.blockType = 'number';
    this.isReadOnly = this.data.get("readOnly") === "true";
    this.decimalPlaces = parseInt(this.data.get("decimalPlaces") || "0");
    this.symbol = this.normalizeSymbol(this.data.get("symbol"));
    this.symbolPosition = this.data.get("symbolPosition") || this.defaultPositionFor(this.symbol);
    this.compareEnabled = this.data.get("compareEnabled") === "true";
    this.comparisonProjectId = this.data.get("comparisonProjectId");

    if (this.hasInputFieldTarget) {
      this.inputFieldTarget.addEventListener("keypress", this.handleKeyPress.bind(this));
    }

    this.saveTimeout = null;

    this.boundHandleComparisonProjectUpdate = this.handleComparisonProjectUpdate.bind(this);
    this.boundHandleComparisonDisplayUpdate = this.handleComparisonDisplayUpdate.bind(this);
    this.boundHandleComparisonDisplayRemove = this.handleComparisonDisplayRemove.bind(this);

    
    if (this.hasComparisonSelectTarget) {
      this.kanbanSubscription = consumer.subscriptions.create("KanbanChannel", {
        received: this.handleKanbanBroadcast.bind(this)
      });
      
      this.comparisonSelectTarget.addEventListener('comparisonProjectUpdated', this.boundHandleComparisonProjectUpdate);
    }
    
    this.element.addEventListener('comparisonDisplayUpdate', this.boundHandleComparisonDisplayUpdate);
    this.element.addEventListener('comparisonDisplayRemove', this.boundHandleComparisonDisplayRemove);
  }

  edit(event) {
    if (this.isReadOnly) return;

    window.activeEditingManager.startEditing(this.blockId, this.blockType, this.element);

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    const displayElement = this.numberDisplayTarget;
    const inputElement = this.inputFieldTarget;
    const displayRow = displayElement.closest('.number-display-row');

    const displayRect = displayElement.getBoundingClientRect();
    const rowRect = displayRow ? displayRow.getBoundingClientRect() : displayRect;

    this.originalValue = inputElement.value || "";

    displayElement.style.visibility = "hidden";

    inputElement.style.display = "block";
    inputElement.style.position = "absolute";
    inputElement.style.top = `${displayRect.top - rowRect.top}px`;
    inputElement.style.left = `${displayRect.left - rowRect.left}px`;
    inputElement.style.width = `${displayRect.width}px`;
    inputElement.style.height = `${displayRect.height}px`;
    inputElement.style.boxSizing = "border-box";
    inputElement.style.zIndex = "10";

    if (displayRow) {
      const currentPosition = window.getComputedStyle(displayRow).position;
      if (currentPosition === 'static') {
        displayRow.style.position = 'relative';
      }
    }

    inputElement.focus();
    inputElement.select();
  }

  handleKeyPress(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
      this.performSave();
    }
  }

  formatDisplay(event) {
    if (!this.hasInputFieldTarget) return;
    
    const value = parseFloat(this.inputFieldTarget.value);
    if (isNaN(value)) return;
    
    // Format the number with proper decimals
    const formatted = this.formatNumber(value);
    
    // Update display immediately for better UX
    if (this.hasNumberDisplayTarget) {
      const displayText = formatted;
      // Don't update the full HTML, just the text content
      const textNode = this.numberDisplayTarget.childNodes[0];
      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        textNode.textContent = displayText;
      }
    }
  }

  formatNumber(value) {
    if (isNaN(value)) return "";
    
    const formatted = this.truncateToDecimalPlaces(value, this.decimalPlaces);
    
    // Add thousands separator
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const numberStr = parts.join('.');
    
    // Add symbol
    if (this.symbol) {
      if (this.symbolPosition === "front") {
        return `${this.symbol}${numberStr}`;
      } else {
        return `${numberStr}${this.symbol}`;
      }
    }
    
    return numberStr;
  }

  truncateToDecimalPlaces(value, decimalPlaces) {
    const factor = Math.pow(10, decimalPlaces);
    const truncated = Math.trunc(value * factor) / factor;

    return truncated.toFixed(decimalPlaces);
  }

  defaultPositionFor(symbol) {
    const frontSymbols = ["$", "£", "€", "¥"];
    return frontSymbols.includes(symbol) ? "front" : "back";
  }

  normalizeSymbol(symbol) {
    if (!symbol || symbol === "none") {
      return "";
    }

    return symbol;
  }

  save(event) {
    if (event.type === "keydown" && event.key !== "Enter") return;

    this.scheduleSave();
  }

  scheduleSave() {
    clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(() => this.performSave(), 3000);
  }

  performSave() {
    if (!this.hasInputFieldTarget || !this.hasNumberDisplayTarget) return;

    const inputElement = this.inputFieldTarget;
    const displayElement = this.numberDisplayTarget;
    const newValue = inputElement.value.trim();
    const originalValue = this.originalValue || "";

    if (newValue === originalValue) {
      this.stopEditing();
      return;
    }

    const { accountId, projectId, taskListId, taskId, blockId } = this.element.dataset;

    fetch(`/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${blockId}/update_content`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        task_block: { value: newValue },
        skip_broadcast: false 
      })
    })
      .then(response => {
        if (response.status === 404) {
          return response.json().then(data => {
            throw new Error(data.error || 'Task block does not exist or has been deleted');
          });
        }
        return response.json();
      })
      .then(data => {
        if (data.success) {
          const savedValue = data.value || "";
          let formatted = "";
          if (savedValue !== "") {
            formatted = this.formatNumber(parseFloat(savedValue));
          }
          
          const numberValueSpan = displayElement.querySelector('.number-value');
          if (numberValueSpan) {
            numberValueSpan.textContent = formatted;
          } else if (formatted) {
            const span = document.createElement('span');
            span.className = 'number-value';
            span.textContent = formatted;
            displayElement.innerHTML = '';
            displayElement.appendChild(span);
          } else {
            displayElement.innerHTML = "";
          }
          
          const copyButton = displayElement.querySelector('.copy_button');
          if (savedValue && formatted) {
            if (copyButton) {
              copyButton.setAttribute('data-copy-link-content', savedValue);
            } else {

            }
          } else if (copyButton) {
            copyButton.remove();
          }
          

          inputElement.value = savedValue;
          
          // Update comparison display if comparison data is provided
          if (data.comparison_data) {
            this.updateComparisonDisplay(data.comparison_data);
          } else {
            
            this.removeComparisonDisplay();
          }
          
          this.refreshFormulaBlocks();
          
          this.stopEditing();
        } else {
          console.error("Failed to save number input:", data);
          if (data.error) {
            alert(data.error);
          }
          this.stopEditing(1000);
        }
      })
      .catch(error => {
        console.error("Error updating block:", error);
        alert(error.message || "This field has been deleted. Please refresh the page.");
        this.stopEditing(1000);
      });
  }

  stopEditing(delay = 500) {
    window.activeEditingManager.stopEditing(this.blockId, delay);
    
    if (this.hasInputFieldTarget) {
      const inputElement = this.inputFieldTarget;
      inputElement.style.display = "none";
      inputElement.style.position = "";
      inputElement.style.top = "";
      inputElement.style.left = "";
      inputElement.style.width = "";
      inputElement.style.height = "";
      inputElement.style.boxSizing = "";
      inputElement.style.zIndex = "";
    }
    if (this.hasNumberDisplayTarget) {
      const displayElement = this.numberDisplayTarget;
      displayElement.style.visibility = "visible";
      displayElement.style.display = "flex";
      
      const displayRow = displayElement.closest('.number-display-row');
      if (displayRow) {
        displayRow.style.display = '';
      }
    }
  }

  updateComparison(event) {
    const projectId = event.target.value;
    const { accountId, projectId: currentProjectId, taskListId, taskId, blockId } = this.element.dataset;

    // Update via the standard update endpoint
    const formData = new FormData();
    formData.append('task_block[comparison_project_id]', projectId || '');
    formData.append('authenticity_token', document.querySelector('meta[name="csrf-token"]').content);

    fetch(`/${accountId}/projects/${currentProjectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${blockId}`, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      },
      body: formData
    })
    .then(response => {
      // Check if response is JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        return response.text().then(text => {
          throw new Error(`Server returned non-JSON response: ${response.status} ${response.statusText}`);
        });
      }
      
      // Check if response is ok
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.error || data.message || `Server error: ${response.status}`);
        });
      }
      
      return response.json();
    })
    .then(data => {
      if (data.success) {
        this.comparisonProjectId = projectId || null;
        
        // Update only the comparison display, not the entire page
        if (data.comparison_data) {
          this.updateComparisonDisplay(data.comparison_data);
        } else {
          // No comparison data available - remove existing comparison display
          this.removeComparisonDisplay();
        }
      } else {
        console.error("Failed to update comparison project:", data);
      }
    })
    .catch(error => {
      console.error("Error updating comparison project:", error);
    });
  }

  updateComparisonDisplay(comparisonData) {
    this.removeComparisonDisplay();
    
    const numberInputWrapper = this.element.querySelector('.number-input-wrapper');
    if (!numberInputWrapper) return;
    
    // Find or get the number-display-row
    let displayRow = numberInputWrapper.querySelector('.number-display-row');
    if (!displayRow) {
      // Create display row if it doesn't exist
      displayRow = document.createElement('div');
      displayRow.className = 'number-display-row';
      const numberDisplay = numberInputWrapper.querySelector('[data-target="number-input.numberDisplay"]');
      if (numberDisplay) {
        numberInputWrapper.insertBefore(displayRow, numberDisplay);
        displayRow.appendChild(numberDisplay);
      }
    }
    
    // Check if a comparison wrapper already exists (shouldn't after removeComparisonDisplay, but double-check)
    let comparisonWrapper = displayRow.querySelector('.number-comparison-wrapper');
    if (!comparisonWrapper) {
      comparisonWrapper = document.createElement('div');
      comparisonWrapper.className = 'number-comparison-wrapper';
      displayRow.appendChild(comparisonWrapper);
    }
    
    // Check if comparison display already exists inside wrapper
    let comparisonDisplay = comparisonWrapper.querySelector('[data-target="number-input.comparisonDisplay"]');
    if (!comparisonDisplay) {
      comparisonDisplay = document.createElement('span');
      comparisonDisplay.setAttribute('data-target', 'number-input.comparisonDisplay');
      comparisonWrapper.appendChild(comparisonDisplay);
    }
    
    comparisonDisplay.className = `number-comparison ${comparisonData.color_class}`;
    comparisonDisplay.innerHTML = `
      <span class="icon comparison-icon ${comparisonData.icon}"></span>
      <span>${comparisonData.percentage}% (from ${comparisonData.formatted_previous_value})</span>
    `;
    comparisonWrapper.classList.remove('hidden');
  }

  removeComparisonDisplay() {
    const numberInputWrapper = this.element.querySelector('.number-input-wrapper');
    if (numberInputWrapper) {
      // Remove all comparison wrappers from anywhere in the wrapper
      const allComparisonWrappers = numberInputWrapper.querySelectorAll('.number-comparison-wrapper');
      allComparisonWrappers.forEach(wrapper => {
        wrapper.remove();
      });
      
      // Also check the display row specifically
      const displayRow = numberInputWrapper.querySelector('.number-display-row');
      if (displayRow) {
        const rowComparisonWrappers = displayRow.querySelectorAll('.number-comparison-wrapper');
        rowComparisonWrappers.forEach(wrapper => {
          wrapper.remove();
        });
      }
      
      // Also check for comparison displays directly (in case structure is different)
      const comparisonDisplays = numberInputWrapper.querySelectorAll('[data-target="number-input.comparisonDisplay"]');
      comparisonDisplays.forEach(display => {
        const wrapper = display.closest('.number-comparison-wrapper');
        if (wrapper) {
          wrapper.remove();
        } else {
          display.remove();
        }
      });
    }
  }

  refreshFormulaBlocks() {
    const { accountId, projectId, taskListId, taskId } = this.element.dataset;
    
    const taskElement = document.querySelector(`[data-task-id="${taskId}"]`);
    if (!taskElement) return;
    
    const formulaBlocks = taskElement.querySelectorAll('[data-controller*="formula-input"]');
    
    formulaBlocks.forEach(formulaBlockElement => {
      const formulaBlockId = formulaBlockElement.dataset.blockId;
      if (!formulaBlockId) return;
      
      fetch(`/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${formulaBlockId}/refresh_formula_value`, {
        method: "GET",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          
          const formulaDisplay = formulaBlockElement.querySelector('[data-target="formula-input.formulaDisplay"]');
          if (formulaDisplay) {
            const formulaValueSpan = formulaDisplay.querySelector('.formula-value');
            if (data.formatted_value && data.formatted_value !== "") {
              if (formulaValueSpan) {
                formulaValueSpan.textContent = data.formatted_value;
              } else {
                
                const emptySpan = formulaDisplay.querySelector('.formula-empty');
                if (emptySpan) {
                  emptySpan.remove();
                }
                const newValueSpan = document.createElement('span');
                newValueSpan.className = 'formula-value';
                newValueSpan.textContent = data.formatted_value;
                formulaDisplay.insertBefore(newValueSpan, formulaDisplay.firstChild);
              }
              
              
              const copyButton = formulaDisplay.querySelector('.copy_button');
              if (copyButton && data.calculated_value !== null) {
                copyButton.setAttribute('data-copy-link-content', data.calculated_value);
              }
            } else {
              
              if (formulaValueSpan) {
                formulaValueSpan.remove();
              }
              
              // Remove copy button when showing "No formula defined"
              const copyButton = formulaDisplay.querySelector('.copy_button');
              if (copyButton) {
                copyButton.remove();
              }
              
              const emptySpan = formulaDisplay.querySelector('.formula-empty');
              if (!emptySpan) {
                const newEmptySpan = document.createElement('span');
                newEmptySpan.className = 'formula-empty';
                newEmptySpan.textContent = 'No formula defined';
                formulaDisplay.insertBefore(newEmptySpan, formulaDisplay.firstChild);
              }
            }
          }
          
          
          if (data.comparison_data) {
            const numberInputWrapper = formulaBlockElement.querySelector('.number-input-wrapper');
            if (numberInputWrapper) {
              let displayRow = numberInputWrapper.querySelector('.number-display-row');
              if (!displayRow) {
                displayRow = document.createElement('div');
                displayRow.className = 'number-display-row';
                const formulaDisplay = numberInputWrapper.querySelector('[data-target="formula-input.formulaDisplay"]');
                if (formulaDisplay) {
                  numberInputWrapper.insertBefore(displayRow, formulaDisplay);
                  displayRow.appendChild(formulaDisplay);
                }
              }
              
              let comparisonWrapper = displayRow.querySelector('.number-comparison-wrapper');
              if (!comparisonWrapper) {
                comparisonWrapper = document.createElement('div');
                comparisonWrapper.className = 'number-comparison-wrapper';
                displayRow.appendChild(comparisonWrapper);
              }
              
              let comparisonDisplay = comparisonWrapper.querySelector('[data-target="formula-input.comparisonDisplay"]');
              if (!comparisonDisplay) {
                comparisonDisplay = document.createElement('span');
                comparisonDisplay.setAttribute('data-target', 'formula-input.comparisonDisplay');
                comparisonWrapper.appendChild(comparisonDisplay);
              }
              
              comparisonDisplay.className = `number-comparison ${data.comparison_data.color_class}`;
              comparisonDisplay.innerHTML = `
                <span class="icon comparison-icon ${data.comparison_data.icon}"></span>
                <span>${data.comparison_data.percentage}% (from ${data.comparison_data.formatted_previous_value})</span>
              `;
              comparisonWrapper.classList.remove('hidden');
            }
          } else {
            
            const numberInputWrapper = formulaBlockElement.querySelector('.number-input-wrapper');
            if (numberInputWrapper) {
              const comparisonWrapper = numberInputWrapper.querySelector('.number-comparison-wrapper');
              if (comparisonWrapper) {
                comparisonWrapper.classList.add('hidden');
              }
            }
          }
        }
      })
      .catch(error => {
        console.error("Error refreshing formula block:", error);
      });
    });
  }

  handleKanbanBroadcast(data) {
    const { task_data } = data;
    
    if (task_data && task_data.update_type === 'update_project' && task_data.project_id && task_data.updated_title) {
      
      if (this.hasComparisonSelectTarget) {
        const selectElement = this.comparisonSelectTarget;
        const option = selectElement.querySelector(`option[value="${task_data.project_id}"]`);
        if (option) {
          option.textContent = task_data.updated_title;
        }
      }
    }
  }

  handleComparisonProjectUpdate(event) {
    const { comparison_project_id, comparison_data } = event.detail;
    this.comparisonProjectId = comparison_project_id || null;
    
    // Update comparison display if data is provided
    if (comparison_data) {
      this.updateComparisonDisplay(comparison_data);
    } else {
      this.removeComparisonDisplay();
    }
  }

  handleComparisonDisplayUpdate(event) {
    const { comparison_data } = event.detail;
    if (comparison_data) {
      this.updateComparisonDisplay(comparison_data);
    }
  }

  handleComparisonDisplayRemove(event) {
    this.removeComparisonDisplay();
  }

  disconnect() {
    if (this.saveTimeout) clearTimeout(this.saveTimeout);
    if (this.blockId) window.activeEditingManager.stopEditing(this.blockId, 0);
    if (this.kanbanSubscription) {
      consumer.subscriptions.remove(this.kanbanSubscription);
    }
    
    if (this.hasComparisonSelectTarget && this.boundHandleComparisonProjectUpdate) {
      this.comparisonSelectTarget.removeEventListener('comparisonProjectUpdated', this.boundHandleComparisonProjectUpdate);
    }
    
    if (this.boundHandleComparisonDisplayUpdate) {
      this.element.removeEventListener('comparisonDisplayUpdate', this.boundHandleComparisonDisplayUpdate);
    }
    
    if (this.boundHandleComparisonDisplayRemove) {
      this.element.removeEventListener('comparisonDisplayRemove', this.boundHandleComparisonDisplayRemove);
    }
  }
}

